/**
 * Solandia shared utilities: date, age and money helpers.
 *
 * IMPORTANT: nothing in this module reads the wall clock at import time, and the
 * functions that need "now" take it as an explicit parameter. This keeps the
 * package pure and deterministically testable. Callers pass `new Date()` (or a
 * fixed instant in tests).
 */
import type { CurrencyCode, IsoDate, IsoDateTime, MinorUnits } from './types.js';

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Format a Date as an ISO-8601 UTC timestamp string. */
export function toIsoDateTime(d: Date): IsoDateTime {
  return d.toISOString();
}

/** Format a Date as a "YYYY-MM-DD" date-only string (UTC). */
export function toIsoDate(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Parse a "YYYY-MM-DD" string into a UTC Date at midnight. Throws if invalid. */
export function parseIsoDate(dateStr: IsoDate): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) throw new RangeError(`Invalid ISO date: "${dateStr}"`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    throw new RangeError(`Invalid calendar date: "${dateStr}"`);
  }
  return d;
}

/**
 * Compute age in whole years from a birth date.
 * `now` is required — do NOT default it. Pass `new Date()` from the caller.
 */
export function ageFromBirthDate(birthDate: IsoDate, now: Date): number {
  const b = parseIsoDate(birthDate);
  let age = now.getUTCFullYear() - b.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - b.getUTCMonth();
  const dayDiff = now.getUTCDate() - b.getUTCDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }
  return Math.max(0, age);
}

/** Add `days` to a Date, returning a new Date (UTC-safe). */
export function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 86_400_000);
}

/** Whole days between two instants (b - a), truncated toward zero. */
export function daysBetween(a: Date, b: Date): number {
  return Math.trunc((b.getTime() - a.getTime()) / 86_400_000);
}

/** True when two Dates fall on the same UTC calendar day. */
export function isSameUtcDay(a: Date, b: Date): boolean {
  return toIsoDate(a) === toIsoDate(b);
}

/** Start of the UTC day for the given instant. */
export function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

// ---------------------------------------------------------------------------
// Money (integer minor units)
// ---------------------------------------------------------------------------

/** Convert a major-unit number (e.g. 12.34) to minor units (1234). */
export function toMinorUnits(major: number): MinorUnits {
  return Math.round(major * 100);
}

/** Convert minor units (1234) to a major-unit number (12.34). */
export function toMajorUnits(minor: MinorUnits): number {
  return minor / 100;
}

/**
 * Split an amount by a payer percentage (0–100), returning each side's minor
 * units. Rounding remainder is assigned to the payer so the two parts always
 * sum back to `amountMinor`.
 */
export function splitAmount(
  amountMinor: MinorUnits,
  splitPct: number,
): { payerShare: MinorUnits; otherShare: MinorUnits } {
  const pct = clamp(splitPct, 0, 100);
  const payerShare = Math.round((amountMinor * pct) / 100);
  const otherShare = amountMinor - payerShare;
  return { payerShare, otherShare };
}

/**
 * Format minor units as a localized currency string.
 * Defaults to Slovak locale ('sk-SK'), the primary UI language.
 */
export function formatMoney(
  amountMinor: MinorUnits,
  currency: CurrencyCode,
  locale = 'sk-SK',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(toMajorUnits(amountMinor));
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/** Clamp a number to the inclusive [min, max] range. */
export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Initials from a display name, e.g. "Anna Nováková" → "AN". */
export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}
